import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Heading,
  Flex,
  IconButton,
  Tooltip,
  Image,
  Input,
  FormControl,
  FormLabel,
  VStack,
  Select,
  SimpleGrid,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Spacer,
  useToast,
  TabPanel,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import UuidPreview from "./UuidPreview";
import { useAuthenticatedRequest } from "./hooks/useAuthenticatedRequest";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlineSave,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlinePencil,
} from "react-icons/hi";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import ClockSelect from "./ClockSelect";
import CodesetSelect from "./CodesetSelect";
import PartySelect from "./PartySelect";
import PartySelectById from "./PartySelectById";
import DocumentDefinitionSelectById from "./DocumentDefinitionSelectById";

ModuleRegistry.registerModules([AllCommunityModule]);

const ProductDefinitionEditor = ({
  product,
  isNew: initialIsNew,
  onSave,
  onBack,
}) => {
  const cardBg = useColorModeValue("white", "gray.700");
  const { get, post, put, getHeaders } = useAuthenticatedRequest();
  const [localProduct, setLocalProduct] = useState(product || {});
  const [isNew, setIsNew] = useState(initialIsNew ?? false);
  const toast = useToast();
  const navigate = useNavigate();
  const [registers, setRegisters] = useState([]);
  const [registerLoading, setRegisterLoading] = useState(true);
  const [registerEntriesCache, setRegisterEntriesCache] = useState({}); // { [registerId]: [...entries] }
  const [docRepoCache, setDocRepoCache] = useState({}); // { [repoId]: { name, createdDate } }
  const [creatingTemplateIndex, setCreatingTemplateIndex] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateDefinitionId, setNewTemplateDefinitionId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [documentDefinitions, setDocumentDefinitions] = useState({});
  const [definitionsLoading, setDefinitionsLoading] = useState(true);
  const templatesGridRef = useRef(null);

  const DOCS_API = "https://documents.poc.mrc.ai/api/documents";
  const DEFINITIONS_API = "https://documents.poc.mrc.ai/api/definitions";

  useEffect(() => {
    const fetchRegisters = async () => {
      try {
        const res = await fetch("https://registers.poc.mrc.ai/api/registers");
        if (!res.ok) throw new Error("Failed to fetch registers");
        const data = await res.json();
        setRegisters(data);
      } catch (err) {
      } finally {
        setRegisterLoading(false);
      }
    };

    fetchRegisters();
  }, []);

  useEffect(() => {
    const fetchDocumentDefinitions = async () => {
      try {
        setDefinitionsLoading(true);
        const res = await get(DEFINITIONS_API);
        const data = res.data;

        const definitionsMap = {};
        if (Array.isArray(data)) {
          data.forEach(def => {
            if (def._id) {
              definitionsMap[def._id] = def.name || "Unnamed Definition";
            }
          });
        }
        setDocumentDefinitions(definitionsMap);
      } catch (err) {
      } finally {
        setDefinitionsLoading(false);
      }
    };

    fetchDocumentDefinitions();
  }, []);

  // Fetch register entries when needed
  const fetchRegisterEntries = async (registerId) => {
    if (!registerId || registerEntriesCache[registerId]) {
      return; // Already cached
    }

    try {
      const response = await fetch(
        `https://registers.poc.mrc.ai/api/registerentries?registerId=${registerId}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch register entries");
      }
      const data = await response.json();
      setRegisterEntriesCache((prev) => ({
        ...prev,
        [registerId]: data,
      }));
    } catch (error) {
      setRegisterEntriesCache((prev) => ({
        ...prev,
        [registerId]: [], // Cache empty array on error
      }));
    }
  };

  // Prefetch entries for all registers used in controls
  useEffect(() => {
    if (localProduct.controls && !registerLoading) {
      localProduct.controls.forEach((control) => {
        if (control.registerType) {
          fetchRegisterEntries(control.registerType);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localProduct.controls, registerLoading]);
  const handleChange = (field, value) => {
    setLocalProduct((prev) => ({ ...prev, [field]: value }));
  };

  // Ensure all register entries have IDs
  useEffect(() => {
    let needsUpdate = false;
    const updatedProduct = { ...localProduct };

    const registerFields = [
      'signalRegister',
      'triggerRegister',
      'eventRegister',
      'productRegister',
      'workflowRegister',
      'controls'
    ];

    registerFields.forEach(field => {
      if (Array.isArray(updatedProduct[field])) {
        const updated = updatedProduct[field].map(item => {
          if (!item.id) {
            needsUpdate = true;
            return { ...item, id: crypto.randomUUID() };
          }
          return item;
        });
        updatedProduct[field] = updated;
      }
    });

    if (needsUpdate) {
      setLocalProduct(updatedProduct);
    }
  }, []);

  const handleSave = async () => {
    try {
      const url = isNew
        ? `https://poc.mrc.ai/${localProduct.code}/api/product`
        : `https://poc.mrc.ai/${localProduct.code}/api/product/${localProduct._id}`;

      const method = isNew ? "POST" : "PUT";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(localProduct),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to ${isNew ? "create" : "update"} product. Status: ${
            response.status
          }`
        );
      }

      const result = await response.json();

      toast({
        title: isNew ? "Product created" : "Product saved",
        description: isNew
          ? "New product was added."
          : "Changes saved successfully.",
        status: "success",
        duration: 3000,
      });

      if (isNew && result.code) {
        setIsNew(false);
        setLocalProduct(result);
        if (onSave) onSave(result, true);
        navigate(`/poc/product/${result.code}/edit`, {
          state: { isNew: false },
        });
      } else {
        if (onSave) onSave(result, false);
      }
    } catch (error) {
      toast({
        title: "Save failed",
        description:
          error.message || "An error occurred while saving the product.",
        status: "error",
        duration: 4000,
      });
    }
  };

  const handleBack = () => {
    if (onBack) onBack();
    else navigate("/poc", { replace: true });
  };

  const handleOpenCreateTemplateModal = () => {
    setNewTemplateName("Product Template Document");
    setNewTemplateDefinitionId(null);
    onOpen();
  };

  const handleCreateTemplateDocument = async () => {
    if (!newTemplateDefinitionId) {
      toast({
        title: "Document Definition Required",
        description: "Please select a document definition",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!newTemplateName.trim()) {
      toast({
        title: "Template Name Required",
        description: "Please enter a template name",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setCreating(true);

      const res = await post(DOCS_API, {
        definitionId: newTemplateDefinitionId,
        name: newTemplateName.trim(),
        isTemplate: true,
        productCode: localProduct.code,
      });

      const created = res.data;

      // 1) cache the name so we can display immediately
      setDocRepoCache((m) => ({
        ...m,
        [created._id]: {
          name: created.name,
          createdDate: created.createdDate,
        },
      }));

      // 2) Add new template entry to productTemplates array
      const updated = [...(localProduct.productTemplates || [])];
      updated.push({
        id: crypto.randomUUID(),
        definitionId: newTemplateDefinitionId,
        documentRepositoryId: created._id,
        createdDate: created.createdDate || new Date().toISOString(),
        status: created.status || "new",
      });
      handleChange("productTemplates", updated);

      toast({
        title: "Template created",
        status: "success",
        duration: 2000,
      });

      onClose();

      // 3) Auto-save the product
      await handleSave();
    } catch (error) {
      toast({
        title: "Failed to create template",
        description: error.message,
        status: "error",
      });
    } finally {
      setCreating(false);
    }
  };

  // Hydrate template names on mount
  useEffect(() => {
    if (localProduct.productTemplates && localProduct.productTemplates.length > 0) {
      localProduct.productTemplates.forEach(template => {
        if (template.documentRepositoryId) {
          hydrateTemplateDocName(template.documentRepositoryId);
        }
      });
    }
  }, [localProduct.productTemplates]);

  // Hydrate document name from repository
  const hydrateTemplateDocName = async (repoId) => {
    if (!repoId || docRepoCache[repoId]) return;
    try {
      const res = await get(`${DOCS_API}/${repoId}`);
      const doc = res.data;

      // Cache the document name
      setDocRepoCache((m) => ({
        ...m,
        [repoId]: { name: doc?.name ?? repoId, createdDate: doc?.createdDate },
      }));

      // If the document has a definitionId, update the template entry
      if (doc?.definitionId) {
        const templateIndex = localProduct.productTemplates?.findIndex(
          t => t.documentRepositoryId === repoId
        );

        if (templateIndex !== -1 && !localProduct.productTemplates[templateIndex].definitionId) {
          const updated = [...(localProduct.productTemplates || [])];
          updated[templateIndex] = {
            ...updated[templateIndex],
            definitionId: doc.definitionId,
          };
          handleChange("productTemplates", updated);
        }
      }
    } catch (err) {
      // fallback to id as name if GET is not available
      setDocRepoCache((m) => ({ ...m, [repoId]: { name: repoId } }));

      // Show toast for 401 errors
      if (err.response?.status === 401) {
        toast({
          title: "Session Expired",
          description: "Your session has expired. Please refresh the page to log in again.",
          status: "error",
          duration: 10000,
          isClosable: true,
        });
      }
    }
  };

  // Update document name in the repository
  const updateTemplateDocumentName = async (repoId, newName) => {
    if (!repoId || !newName) return;

    try {
      // First, fetch the current document to get all its fields
      const getRes = await get(`${DOCS_API}/${repoId}`);
      const currentDoc = getRes.data;

      // Now update with the new name, preserving all other fields
      const res = await put(`${DOCS_API}/${repoId}`, {
        ...currentDoc,
        name: newName,
      });

      const updated = res.data;

      // Update cache
      setDocRepoCache((m) => ({
        ...m,
        [repoId]: { name: updated?.name ?? newName, createdDate: updated?.createdDate },
      }));

      toast({
        title: "Template name updated",
        status: "success",
        duration: 2000,
      });
    } catch (err) {
      // Check for 401 unauthorized
      if (err.response?.status === 401) {
        toast({
          title: "Session Expired",
          description: "Your session has expired. Please refresh the page to log in again.",
          status: "error",
          duration: 10000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error updating template name",
          description: err.message,
          status: "error",
        });
      }
    }
  };

  // Hydrate all template document names
  useEffect(() => {
    const templates = Array.isArray(localProduct.productTemplates)
      ? localProduct.productTemplates
      : [];
    templates
      .map((t) => t?.documentRepositoryId)
      .filter((id) => !!id && !docRepoCache[id])
      .forEach((id) => hydrateTemplateDocName(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localProduct.productTemplates]);

  const updateOracleField = (
    apdIdx,
    stageIdx,
    activityIdx,
    oracleIdx,
    field,
    value
  ) => {
    const updated = [...(localProduct.activityPatternDefinitions || [])];
    const oracleRef =
      updated?.[apdIdx]?.stages?.[stageIdx]?.activities?.[activityIdx]
        ?.oracles?.[oracleIdx];
    if (!oracleRef) return;

    if (field === "party") {
      // value should be a string ID or null
      oracleRef.party = value ?? null;
    } else {
      oracleRef[field] = value ?? null;
    }

    handleChange("activityPatternDefinitions", updated);
  };

  // AG Grid cell renderers for Product Templates
  const EditTemplateCellRenderer = (params) => {
    const repoId = params.data?.documentRepositoryId;
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Tooltip label="Edit Template">
          <IconButton
            icon={<HiOutlinePencil />}
            size="sm"
            onClick={() => {
              if (repoId) {
                navigate(`/editor/${localProduct.code}/template?doc=${repoId}`);
              }
            }}
            isDisabled={!repoId}
            aria-label="Edit Template"
          />
        </Tooltip>
      </Box>
    );
  };

  const DeleteTemplateCellRenderer = (params) => {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Tooltip label="Remove Template">
          <IconButton
            icon={<HiOutlineTrash />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={() => {
              const updated = [...localProduct.productTemplates];
              const idx = localProduct.productTemplates.findIndex(t => t.id === params.data.id);
              if (idx !== -1) {
                updated.splice(idx, 1);
                handleChange("productTemplates", updated);
              }
            }}
            aria-label="Remove Template"
          />
        </Tooltip>
      </Box>
    );
  };

  const DefinitionCellRenderer = (params) => {
    if (definitionsLoading) return "Loading...";
    // params.value is the definitionId from the field
    const definitionId = params.value || params.data?.definitionId;
    if (!definitionId) return "—";
    const defName = documentDefinitions[definitionId];
    return defName || `Missing: ${definitionId}`;
  };

  const TemplateNameCellRenderer = (params) => {
    const repoId = params.data?.documentRepositoryId;
    const repoName = repoId ? docRepoCache[repoId]?.name : "";
    return repoName || "Not linked";
  };

  // AG Grid column definitions for Product Templates
  const templateColumnDefs = useMemo(
    () => [
      {
        headerName: "",
        field: "edit",
        cellRenderer: EditTemplateCellRenderer,
        width: 60,
        sortable: false,
        filter: false,
        suppressMenu: true,
        pinned: "left",
      },
      {
        headerName: "Template Name",
        field: "documentRepositoryId",
        flex: 2,
        sortable: true,
        filter: true,
        cellRenderer: TemplateNameCellRenderer,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Type",
        field: "definitionId",
        flex: 2,
        sortable: true,
        filter: true,
        cellRenderer: DefinitionCellRenderer,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "",
        field: "delete",
        cellRenderer: DeleteTemplateCellRenderer,
        width: 60,
        sortable: false,
        filter: false,
        suppressMenu: true,
      },
    ],
    [documentDefinitions, docRepoCache, localProduct.productTemplates, definitionsLoading]
  );

  const templateDefaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
    }),
    []
  );

  // Default column definition for register tables
  const registerDefaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      editable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
    }),
    []
  );

  // Cell renderer for delete buttons in registers
  const DeleteRegisterCellRenderer = (registerType) => (params) => {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Tooltip label="Delete">
          <IconButton
            icon={<HiOutlineTrash />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={() => {
              const updated = [...(localProduct[registerType] || [])];
              const idx = updated.findIndex(item => item.id === params.data.id);
              if (idx !== -1) {
                updated.splice(idx, 1);
                handleChange(registerType, updated);
              }
            }}
            aria-label="Delete"
          />
        </Tooltip>
      </Box>
    );
  };

  // Custom cell editor for CodesetSelect fields
  const CodesetCellEditor = React.forwardRef((props, ref) => {
    const { value, onValueChange, codesetCode } = props;

    return (
      <Box width="100%" p={1}>
        <CodesetSelect
          codesetCode={codesetCode}
          value={value ? { code: value, description: "" } : null}
          onChange={(selected) => {
            onValueChange(selected?.code || "");
          }}
          size="xs"
        />
      </Box>
    );
  });

  // Custom cell editor for Activity Pattern dropdown
  const ActivityPatternCellEditor = React.forwardRef((props, ref) => {
    const { value, onValueChange } = props;

    return (
      <Box width="100%" p={1}>
        <Select
          value={value || ""}
          onChange={(e) => onValueChange(e.target.value)}
          size="xs"
        >
          <option value="">Select...</option>
          {(localProduct.activityPatternDefinitions || []).map((pattern) => (
            <option key={pattern._id} value={pattern._id}>
              {pattern.name}
            </option>
          ))}
        </Select>
      </Box>
    );
  });

  // Custom cell editor for Register Type dropdown
  const RegisterTypeCellEditor = React.forwardRef((props, ref) => {
    const { value, onValueChange } = props;

    return (
      <Box width="100%" p={1}>
        <Select
          value={value || ""}
          onChange={(e) => onValueChange(e.target.value)}
          size="xs"
        >
          <option value="">Select Register</option>
          {registers.map((reg) => (
            <option key={reg._id} value={reg._id}>
              {reg.name}
            </option>
          ))}
        </Select>
      </Box>
    );
  });

  // Custom cell editor for Entry Type dropdown
  const EntryTypeCellEditor = React.forwardRef((props, ref) => {
    const { value, onValueChange, data } = props;
    const registerType = data?.registerType;

    return (
      <Box width="100%" p={1}>
        <Select
          value={value || ""}
          onChange={(e) => onValueChange(e.target.value)}
          size="xs"
          isDisabled={!registerType}
        >
          <option value="">Select Register Entry</option>
          {registerType && registerEntriesCache[registerType]?.map((entry) => (
            <option key={entry._id} value={entry._id}>
              {entry.name}
            </option>
          ))}
        </Select>
      </Box>
    );
  });

  // Signals Register column definitions
  const signalRegisterColumnDefs = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Type",
        field: "type",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Route",
        field: "route",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Object Type",
        field: "objectType",
        flex: 1,
        editable: true,
        cellEditor: (props) => <CodesetCellEditor {...props} codesetCode="SLC009" />,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Standard Payload",
        field: "standardPayloadType",
        flex: 1,
        editable: true,
        cellEditor: (props) => <CodesetCellEditor {...props} codesetCode="B58" />,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "",
        field: "delete",
        cellRenderer: DeleteRegisterCellRenderer("signalRegister"),
        width: 60,
        sortable: false,
        filter: false,
        editable: false,
        suppressMenu: true,
      },
    ],
    [localProduct.signalRegister]
  );

  // Triggers Register column definitions
  const triggerRegisterColumnDefs = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Type",
        field: "type",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Route",
        field: "route",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Workflow",
        field: "workflow",
        flex: 1,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Object Type",
        field: "objectType",
        flex: 1,
        editable: true,
        cellEditor: (props) => <CodesetCellEditor {...props} codesetCode="SLC009" />,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Standard Payload",
        field: "standardPayloadType",
        flex: 1,
        editable: true,
        cellEditor: (props) => <CodesetCellEditor {...props} codesetCode="B58" />,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "",
        field: "delete",
        cellRenderer: DeleteRegisterCellRenderer("triggerRegister"),
        width: 60,
        sortable: false,
        filter: false,
        editable: false,
        suppressMenu: true,
      },
    ],
    [localProduct.triggerRegister]
  );

  // Events Register column definitions
  const eventRegisterColumnDefs = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Type",
        field: "type",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Route",
        field: "route",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Object Type",
        field: "objectType",
        flex: 1,
        editable: true,
        cellEditor: (props) => <CodesetCellEditor {...props} codesetCode="SLC009" />,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Standard Payload",
        field: "standardPayloadType",
        flex: 1,
        editable: true,
        cellEditor: (props) => <CodesetCellEditor {...props} codesetCode="B58" />,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "",
        field: "delete",
        cellRenderer: DeleteRegisterCellRenderer("eventRegister"),
        width: 60,
        sortable: false,
        filter: false,
        editable: false,
        suppressMenu: true,
      },
    ],
    [localProduct.eventRegister]
  );

  // Product Register column definitions
  const productRegisterColumnDefs = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Type",
        field: "type",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Route",
        field: "route",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Object Type",
        field: "objectType",
        flex: 1,
        editable: true,
        cellEditor: (props) => <CodesetCellEditor {...props} codesetCode="SLC009" />,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Standard Payload",
        field: "standardPayloadType",
        flex: 1,
        editable: true,
        cellEditor: (props) => <CodesetCellEditor {...props} codesetCode="B58" />,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "",
        field: "delete",
        cellRenderer: DeleteRegisterCellRenderer("productRegister"),
        width: 60,
        sortable: false,
        filter: false,
        editable: false,
        suppressMenu: true,
      },
    ],
    [localProduct.productRegister]
  );

  // Workflow Register column definitions
  const workflowRegisterColumnDefs = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Type",
        field: "type",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Route",
        field: "route",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Activity Pattern",
        field: "activityPatternId",
        flex: 1,
        editable: true,
        cellEditor: ActivityPatternCellEditor,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Object Type",
        field: "objectType",
        flex: 1,
        editable: true,
        cellEditor: (props) => <CodesetCellEditor {...props} codesetCode="SLC009" />,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Standard Payload",
        field: "standardPayloadType",
        flex: 1,
        editable: true,
        cellEditor: (props) => <CodesetCellEditor {...props} codesetCode="B58" />,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "",
        field: "delete",
        cellRenderer: DeleteRegisterCellRenderer("workflowRegister"),
        width: 60,
        sortable: false,
        filter: false,
        editable: false,
        suppressMenu: true,
      },
    ],
    [localProduct.workflowRegister, localProduct.activityPatternDefinitions]
  );

  // Controls column definitions
  const controlsColumnDefs = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        flex: 2,
        editable: true,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Register Type",
        field: "registerType",
        flex: 2,
        editable: true,
        cellEditor: RegisterTypeCellEditor,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Entry Type",
        field: "entryType",
        flex: 2,
        editable: true,
        cellEditor: EntryTypeCellEditor,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "Signal Type",
        field: "signal",
        flex: 1,
        editable: true,
        cellEditor: (props) => <CodesetCellEditor {...props} codesetCode="SLC008" />,
        cellStyle: { display: "flex", alignItems: "center" },
      },
      {
        headerName: "",
        field: "delete",
        cellRenderer: DeleteRegisterCellRenderer("controls"),
        width: 60,
        sortable: false,
        filter: false,
        editable: false,
        suppressMenu: true,
      },
    ],
    [localProduct.controls, registers, registerEntriesCache]
  );

  // Handle cell value changes for all registers
  const onCellValueChanged = (params, registerType) => {
    const updated = [...(localProduct[registerType] || [])];
    const idx = updated.findIndex(item => item.id === params.data.id);
    if (idx !== -1) {
      updated[idx] = params.data;
      handleChange(registerType, updated);
    }
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />
        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip label="Back to the Environment Homepage">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleBack}
                  />
                </Tooltip>
                <Heading size="lg">
                  {localProduct.name} Product Definition
                </Heading>
                <FormLabel>({localProduct.code})</FormLabel>
                <Tooltip label="Save Product Definition">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <UuidPreview size="xs" uuid={localProduct._id} />

            <FormControl>
              <FormLabel fontSize="xs">Description</FormLabel>
              <Input
                size="xs"
                value={localProduct.description}
                onChange={(e) => handleChange("description", e.target.value)}
              />
            </FormControl>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <ClockSelect
                label="Clock"
                value={localProduct.clockId} // <-- this matches a clock._id
                onChange={(clockId) => handleChange("clockId", clockId)} // <-- stores the selected _id in product.clockId
                size="xs"
                height="40px"
              />

              <FormControl>
                <FormLabel fontSize="xs">Data Standard Version</FormLabel>
                <Input
                  size="xs"
                  value={localProduct.dataStandardVersionCode}
                  onChange={(e) =>
                    handleChange("dataStandardVersionCode", e.target.value)
                  }
                />
              </FormControl>
            </SimpleGrid>
          </Box>
          <Box
            borderWidth="1px"
            borderRadius="xs"
            p={4}
            borderColor={"gray.500"}
            backgroundColor="whiteAlpha.800">
            <Heading size="md" mb={4}>
              Definitions
            </Heading>
            <Tabs
              orientation="horizontal"
              variant="line"
              colorScheme="linkedin">
              <TabList>
                <Tab>Endpoints</Tab>
                <Tab>Signals Register</Tab>
                <Tab>Triggers Register</Tab>
                <Tab>Events Register</Tab>
                <Tab>Product Register</Tab>
                <Tab>Product Templates</Tab>
                <Tab>Workflow Register</Tab>
                <Tab>Activity Patterns</Tab>
                <Tab>Controls</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <SimpleGrid columns={{ base: 1, md: 7 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="xs">Signals</FormLabel>
                      <Input
                        size="xs"
                        value={localProduct.routing?.signalsEndpoint}
                        onChange={(e) =>
                          handleChange(
                            "routing.signalsEndpoint",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Triggers</FormLabel>
                      <Input
                        size="xs"
                        value={localProduct.routing?.triggersEndpoint}
                        onChange={(e) =>
                          handleChange(
                            "routing.triggersEndpoint",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Events</FormLabel>
                      <Input
                        size="xs"
                        value={localProduct.routing?.eventsEndpoint}
                        onChange={(e) =>
                          handleChange("routing.eventsEndpoint", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Product</FormLabel>
                      <Input
                        size="xs"
                        value={localProduct.routing?.productEndpoint}
                        onChange={(e) =>
                          handleChange(
                            "routing.productEndpoint",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Contracts</FormLabel>
                      <Input
                        size="xs"
                        value={localProduct.routing?.contractsEndpoint}
                        onChange={(e) =>
                          handleChange(
                            "routing.contractsEndpoint",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Workflow</FormLabel>
                      <Input
                        size="xs"
                        value={localProduct.routing?.workflowEndpoint}
                        onChange={(e) =>
                          handleChange(
                            "routing.workflowEndpoint",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                  </SimpleGrid>
                </TabPanel>
                <TabPanel>
                  <HStack mb={4}>
                    <Button
                      leftIcon={<HiOutlinePlus />}
                      colorScheme="blue"
                      size="md"
                      onClick={() => {
                        const updated = [
                          ...(localProduct.signalRegister || []),
                        ];
                        updated.push({
                          id: crypto.randomUUID(),
                          name: "",
                          type: "",
                          route: "",
                          objectType: "",
                          standardPayloadType: "",
                        });
                        handleChange("signalRegister", updated);
                      }}
                    >
                      Create
                    </Button>
                  </HStack>

                  {Array.isArray(localProduct.signalRegister) &&
                  localProduct.signalRegister.length > 0 ? (
                    <Box
                      className="ag-theme-quartz"
                      style={{ height: "400px", width: "100%" }}
                    >
                      <AgGridReact
                        rowData={localProduct.signalRegister}
                        columnDefs={signalRegisterColumnDefs}
                        defaultColDef={registerDefaultColDef}
                        rowHeight={50}
                        pagination={true}
                        paginationPageSize={10}
                        animateRows={true}
                        rowSelection="single"
                        getRowId={(params) => params.data.id}
                        onCellValueChanged={(params) => onCellValueChanged(params, "signalRegister")}
                      />
                    </Box>
                  ) : (
                    <Text color="gray.500" fontSize="sm" fontStyle="italic">
                      No signal register entries defined yet.
                    </Text>
                  )}
                </TabPanel>
                <TabPanel>
                  <HStack mb={4}>
                    <Button
                      leftIcon={<HiOutlinePlus />}
                      colorScheme="blue"
                      size="md"
                      onClick={() => {
                        const updated = [
                          ...(localProduct.triggerRegister || []),
                        ];
                        updated.push({
                          id: crypto.randomUUID(),
                          name: "",
                          type: "",
                          route: "",
                          workflow: "",
                          objectType: "",
                          standardPayloadType: "",
                        });
                        handleChange("triggerRegister", updated);
                      }}
                    >
                      Create
                    </Button>
                  </HStack>

                  {Array.isArray(localProduct.triggerRegister) &&
                  localProduct.triggerRegister.length > 0 ? (
                    <Box
                      className="ag-theme-quartz"
                      style={{ height: "400px", width: "100%" }}
                    >
                      <AgGridReact
                        rowData={localProduct.triggerRegister}
                        columnDefs={triggerRegisterColumnDefs}
                        defaultColDef={registerDefaultColDef}
                        rowHeight={50}
                        pagination={true}
                        paginationPageSize={10}
                        animateRows={true}
                        rowSelection="single"
                        getRowId={(params) => params.data.id}
                        onCellValueChanged={(params) => onCellValueChanged(params, "triggerRegister")}
                      />
                    </Box>
                  ) : (
                    <Text color="gray.500" fontSize="sm" fontStyle="italic">
                      No trigger register entries defined yet.
                    </Text>
                  )}
                </TabPanel>
                <TabPanel>
                  <HStack mb={4}>
                    <Button
                      leftIcon={<HiOutlinePlus />}
                      colorScheme="blue"
                      size="md"
                      onClick={() => {
                        const updated = [
                          ...(localProduct.eventRegister || []),
                        ];
                        updated.push({
                          id: crypto.randomUUID(),
                          name: "",
                          type: "",
                          route: "",
                          objectType: "",
                          standardPayloadType: "",
                        });
                        handleChange("eventRegister", updated);
                      }}
                    >
                      Create
                    </Button>
                  </HStack>

                  {Array.isArray(localProduct.eventRegister) &&
                  localProduct.eventRegister.length > 0 ? (
                    <Box
                      className="ag-theme-quartz"
                      style={{ height: "400px", width: "100%" }}
                    >
                      <AgGridReact
                        rowData={localProduct.eventRegister}
                        columnDefs={eventRegisterColumnDefs}
                        defaultColDef={registerDefaultColDef}
                        rowHeight={50}
                        pagination={true}
                        paginationPageSize={10}
                        animateRows={true}
                        rowSelection="single"
                        getRowId={(params) => params.data.id}
                        onCellValueChanged={(params) => onCellValueChanged(params, "eventRegister")}
                      />
                    </Box>
                  ) : (
                    <Text color="gray.500" fontSize="sm" fontStyle="italic">
                      No event register entries defined yet.
                    </Text>
                  )}
                </TabPanel>

                <TabPanel>
                  <HStack mb={4}>
                    <Button
                      leftIcon={<HiOutlinePlus />}
                      colorScheme="blue"
                      size="md"
                      onClick={() => {
                        const updated = [
                          ...(localProduct.productRegister || []),
                        ];
                        updated.push({
                          id: crypto.randomUUID(),
                          name: "",
                          type: "",
                          route: "",
                          objectType: "",
                          standardPayloadType: "",
                        });
                        handleChange("productRegister", updated);
                      }}
                    >
                      Create
                    </Button>
                  </HStack>

                  {Array.isArray(localProduct.productRegister) &&
                  localProduct.productRegister.length > 0 ? (
                    <Box
                      className="ag-theme-quartz"
                      style={{ height: "400px", width: "100%" }}
                    >
                      <AgGridReact
                        rowData={localProduct.productRegister}
                        columnDefs={productRegisterColumnDefs}
                        defaultColDef={registerDefaultColDef}
                        rowHeight={50}
                        pagination={true}
                        paginationPageSize={10}
                        animateRows={true}
                        rowSelection="single"
                        getRowId={(params) => params.data.id}
                        onCellValueChanged={(params) => onCellValueChanged(params, "productRegister")}
                      />
                    </Box>
                  ) : (
                    <Text color="gray.500" fontSize="sm" fontStyle="italic">
                      No product register entries defined yet.
                    </Text>
                  )}
                </TabPanel>










                <TabPanel>
                  <HStack mb={4}>
                    <Button
                      leftIcon={<HiOutlinePlus />}
                      colorScheme="blue"
                      size="md"
                      onClick={handleOpenCreateTemplateModal}
                    >
                      Create
                    </Button>
                  </HStack>

                  {Array.isArray(localProduct.productTemplates) &&
                  localProduct.productTemplates.length > 0 ? (
                    <Box
                      className="ag-theme-quartz"
                      style={{ height: "400px", width: "100%" }}
                    >
                      <AgGridReact
                        ref={templatesGridRef}
                        rowData={localProduct.productTemplates}
                        columnDefs={templateColumnDefs}
                        defaultColDef={templateDefaultColDef}
                        rowHeight={50}
                        pagination={true}
                        paginationPageSize={10}
                        animateRows={true}
                        rowSelection="single"
                        getRowId={(params) => params.data.id}
                      />
                    </Box>
                  ) : (
                    <Text color="gray.500" fontSize="sm" fontStyle="italic">
                      No product templates defined yet.
                    </Text>
                  )}
                </TabPanel>























                <TabPanel>
                  <HStack mb={4}>
                    <Button
                      leftIcon={<HiOutlinePlus />}
                      colorScheme="blue"
                      size="md"
                      onClick={() => {
                        const updated = [
                          ...(localProduct.workflowRegister || []),
                        ];
                        updated.push({
                          id: crypto.randomUUID(),
                          name: "",
                          type: "",
                          route: "",
                          activityPatternId: "",
                          objectType: "",
                          standardPayloadType: "",
                        });
                        handleChange("workflowRegister", updated);
                      }}
                    >
                      Create
                    </Button>
                  </HStack>

                  {Array.isArray(localProduct.workflowRegister) &&
                  localProduct.workflowRegister.length > 0 ? (
                    <Box
                      className="ag-theme-quartz"
                      style={{ height: "400px", width: "100%" }}
                    >
                      <AgGridReact
                        rowData={localProduct.workflowRegister}
                        columnDefs={workflowRegisterColumnDefs}
                        defaultColDef={registerDefaultColDef}
                        rowHeight={50}
                        pagination={true}
                        paginationPageSize={10}
                        animateRows={true}
                        rowSelection="single"
                        getRowId={(params) => params.data.id}
                        onCellValueChanged={(params) => onCellValueChanged(params, "workflowRegister")}
                      />
                    </Box>
                  ) : (
                    <Text color="gray.500" fontSize="sm" fontStyle="italic">
                      No workflow register entries defined yet.
                    </Text>
                  )}
                </TabPanel>

                <TabPanel>
                  <HStack spacing={2} mb={1}>
                    <Heading size="sm" mb={1}>
                      Activity Pattern Definitions
                    </Heading>
                    <Tooltip
                      label="Add Activity Pattern Definition..."
                      aria-label="Add Activity Pattern Definition tooltip">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        size="sm"
                        onClick={() => {
                          const updated = [
                            ...(localProduct.activityPatternDefinitions || []),
                          ];
                          updated.push({
                            id: crypto.randomUUID(),
                            name: "New Activity Pattern Definition",
                            type: "",
                            objectId: null,
                            stages: [],
                          });
                          handleChange("activityPatternDefinitions", updated);
                        }}
                      />
                    </Tooltip>
                  </HStack>

                  {(localProduct.activityPatternDefinitions || []).map(
                    (apd, apdIdx) => (
                      <Box
                        key={apdIdx}
                        borderWidth="1px"
                        borderRadius="md"
                        p={1}
                        mb={1}
                        borderColor="gray.500">
                        <UuidPreview size="xs" uuid={apd.id} />

                        <SimpleGrid columns={{ base: 3, md: 6 }} spacing={4}>
                          <FormControl>
                            <FormLabel fontSize="xs">Name</FormLabel>
                            <Input
                              size="xs"
                              value={apd.name || ""}
                              onChange={(e) => {
                                const newActivityPatterns = [
                                  ...localProduct.activityPatternDefinitions,
                                ];
                                newActivityPatterns[apdIdx] = {
                                  ...newActivityPatterns[apdIdx],
                                  name: e.target.value,
                                };
                                handleChange(
                                  "activityPatternDefinitions",
                                  newActivityPatterns
                                );
                              }}
                            />
                          </FormControl>
                          <CodesetSelect
                            label="Type"
                            codesetCode="SLC007"
                            value={
                              apd.type
                                ? { code: apd.type, description: "" }
                                : null
                            }
                            onChange={(selected) => {
                              const newActivityPatterns = [
                                ...localProduct.activityPatternDefinitions,
                              ];
                              newActivityPatterns[apdIdx].type = selected.code;
                              handleChange(
                                "activityPatternDefinitions",
                                newActivityPatterns
                              );
                            }}
                            size="xs"
                          />
                        </SimpleGrid>

                        <Tabs
                          mt={2}
                          orientation="horizontal"
                          variant="solid-rounded"
                          colorScheme="linkedin">
                          <TabList>
                            {apd.stages?.map((stage, stageidx) => (
                              <Tab fontSize="xs" key={stage.id || stageidx}>
                                Stage {stage.number}
                              </Tab>
                            ))}
                            <IconButton
                              icon={<HiOutlinePlus />}
                              size="sm"
                              onClick={() => {
                                const newActivityPatterns = [
                                  ...localProduct.activityPatternDefinitions,
                                ];
                                if (!newActivityPatterns[apdIdx].stages) {
                                  newActivityPatterns[apdIdx].stages = [];
                                }
                                // Find the next available stage number
                                const usedNumbers = newActivityPatterns[
                                  apdIdx
                                ].stages.map((s) => s.number);
                                let nextNumber = 0;
                                while (usedNumbers.includes(nextNumber)) {
                                  nextNumber++;
                                }
                                newActivityPatterns[apdIdx].stages.push({
                                  id: crypto.randomUUID(),
                                  number: nextNumber,
                                  activities: [],
                                });
                                handleChange(
                                  "activityPatternDefinitions",
                                  newActivityPatterns
                                );
                              }}
                            />
                          </TabList>
                          <TabPanels>
                            {apd.stages?.map((stage, stageIdx) => (
                              <TabPanel key={stage.id || stageIdx}>
                                <Box
                                  borderWidth="1px"
                                  borderRadius="md"
                                  p={1}
                                  mb={1}
                                  borderColor={"gray.500"}>
                                  <SimpleGrid columns={2} spacing={4}>
                                    <FormControl>
                                      <FormLabel fontSize="xs">
                                        Stage#
                                      </FormLabel>
                                      <Input
                                        size="xs"
                                        value={stage.number}
                                        onChange={(e) => {
                                          const updated = [
                                            ...localProduct.activityPatternDefinitions,
                                          ];
                                          updated[apdIdx].stages[
                                            stageIdx
                                          ].number = e.target.value;
                                          handleChange(
                                            "activityPatternDefinitions",
                                            updated
                                          );
                                        }}
                                      />
                                    </FormControl>
                                    <FormControl>
                                      <FormLabel fontSize="xs">
                                        Status
                                      </FormLabel>
                                      <Input
                                        size="xs"
                                        value={stage.status}
                                        onChange={(e) => {
                                          const updated = [
                                            ...localProduct.activityPatternDefinitions,
                                          ];
                                          updated[apdIdx].stages[
                                            stageIdx
                                          ].status = e.target.value;
                                          handleChange(
                                            "activityPatternDefinitions",
                                            updated
                                          );
                                        }}
                                      />
                                    </FormControl>
                                  </SimpleGrid>

                                  <HStack justify="flex-end">
                                    <Tooltip label="Delete stage...">
                                      <IconButton
                                        icon={<HiOutlineTrash />}
                                        size="xs"
                                        onClick={() => {
                                          const newActivityPatterns = [
                                            ...localProduct.activityPatternDefinitions,
                                          ];
                                          newActivityPatterns[
                                            apdIdx
                                          ].stages.splice(stageIdx, 1);
                                          handleChange(
                                            "activityPatternDefinitions",
                                            newActivityPatterns
                                          );
                                        }}
                                      />
                                    </Tooltip>
                                  </HStack>
                                </Box>

                                <HStack>
                                  <Heading size="xs" mb={2}>
                                    Activities
                                  </Heading>
                                  <Tooltip label="Add activity">
                                    <IconButton
                                      icon={<HiOutlinePlus />}
                                      aria-label="Add activity"
                                      size="md"
                                      onClick={() => {
                                        const updated = [
                                          ...localProduct.activityPatternDefinitions,
                                        ];
                                        if (
                                          !updated[apdIdx].stages[stageIdx]
                                            .activities
                                        ) {
                                          updated[apdIdx].stages[
                                            stageIdx
                                          ].activities = [];
                                        }
                                        updated[apdIdx].stages[
                                          stageIdx
                                        ].activities.push({
                                          id: crypto.randomUUID(),
                                          name: "New Activity",
                                          type: "",
                                          oracles: [],
                                        });
                                        handleChange(
                                          "activityPatternDefinitions",
                                          updated
                                        );
                                      }}
                                    />
                                  </Tooltip>
                                </HStack>
                                {stage.activities?.map(
                                  (activity, activityIdx) => (
                                    <Box
                                      key={activityIdx}
                                      bg={cardBg}
                                      p={2}
                                      rounded="lg"
                                      shadow="md"
                                      border="1px solid"
                                      borderColor="gray.500"
                                      minW="200px"
                                      maxW="fit-content"
                                      transition="all 0.2s ease"
                                      _hover={{
                                        shadow: "lg",
                                        transform: "translateY(-4px)",
                                      }}>
                                      <HStack justify="space-between">
                                        <FormControl>
                                          <FormLabel fontSize="xs">
                                            Name
                                          </FormLabel>
                                          <Input
                                            size="xs"
                                            value={activity.name}
                                            onChange={(e) => {
                                              const updated = [
                                                ...localProduct.activityPatternDefinitions,
                                              ];
                                              updated[apdIdx].stages[
                                                stageIdx
                                              ].activities[activityIdx].name =
                                                e.target.value;
                                              handleChange(
                                                "activityPatternDefinitions",
                                                updated
                                              );
                                            }}
                                          />
                                        </FormControl>

                                        <FormControl>
                                          <FormLabel fontSize="xs">
                                            Type
                                          </FormLabel>
                                          <Input
                                            size="xs"
                                            value={activity.type}
                                            onChange={(e) => {
                                              const updated = [
                                                ...localProduct.activityPatternDefinitions,
                                              ];
                                              updated[apdIdx].stages[
                                                stageIdx
                                              ].activities[activityIdx].type =
                                                e.target.value;
                                              handleChange(
                                                "activityPatternDefinitions",
                                                updated
                                              );
                                            }}
                                          />
                                        </FormControl>
                                      </HStack>

                                      <Box
                                        borderWidth="1px"
                                        borderRadius="md"
                                        mt={2}
                                        p={1}
                                        mb={1}
                                        borderColor="gray.500">
                                        <HStack spacing={4} mb={2}>
                                          <Heading size="xs" mb={2}>
                                            Oracles
                                          </Heading>
                                          <Tooltip
                                            label="Add Oracle..."
                                            aria-label="Add Oracle tooltip">
                                            <IconButton
                                              icon={<HiOutlinePlus />}
                                              size="sm"
                                              onClick={() => {
                                                const updated = [
                                                  ...localProduct.activityPatternDefinitions,
                                                ];
                                                updated[apdIdx].stages[
                                                  stageIdx
                                                ].activities[
                                                  activityIdx
                                                ].oracles.push({
                                                  id: crypto.randomUUID(),
                                                  participantFunctionCode: null,
                                                  party: null,
                                                });
                                                handleChange(
                                                  "activityPatternDefinitions",
                                                  updated
                                                );
                                              }}
                                            />
                                          </Tooltip>
                                        </HStack>

                                        <Table
                                          variant="unstyled"
                                          size="sm"
                                          fontSize="xs"
                                          style={{
                                            tableLayout: "auto",
                                            width: "100%",
                                          }}
                                          sx={{
                                            "tbody tr:nth-of-type(odd)": {
                                              backgroundColor: "gray.200",
                                            },
                                          }}>
                                          <Thead>
                                            <Tr>
                                              <Th fontSize="xs">Role</Th>
                                              <Th fontSize="xs">
                                                Participant Function
                                              </Th>
                                              <Th fontSize="xs">Party</Th>
                                              <Th fontSize="xs"></Th>
                                            </Tr>
                                          </Thead>

                                          <Tbody>
                                            {(activity.oracles || []).map(
                                              (oracle, oracleIdx) => (
                                                <Tr key={oracle.id}>
                                                  <Td
                                                    style={{ padding: "8px" }}>
                                                    <CodesetSelect
                                                      size="xs"
                                                      codesetCode="B06"
                                                      value={
                                                        oracle.participantFunctionCode
                                                          ? {
                                                              code: oracle.participantFunctionCode,
                                                              description: "",
                                                            }
                                                          : null
                                                      }
                                                      onChange={(selected) => {
                                                        updateOracleField(
                                                          apdIdx,
                                                          stageIdx,
                                                          activityIdx,
                                                          oracleIdx,
                                                          "participantFunctionCode",
                                                          selected
                                                            ? selected.code
                                                            : null
                                                        );
                                                      }}
                                                    />
                                                  </Td>
                                                  <Td
                                                    style={{ padding: "8px" }}>
                                                    <CodesetSelect
                                                      size="xs"
                                                      codesetCode="3035"
                                                      value={
                                                        oracle.roleCode
                                                          ? {
                                                              code: oracle.roleCode,
                                                              description: "",
                                                            }
                                                          : null
                                                      }
                                                      onChange={(selected) => {
                                                        updateOracleField(
                                                          apdIdx,
                                                          stageIdx,
                                                          activityIdx,
                                                          oracleIdx,
                                                          "roleCode",
                                                          selected
                                                            ? selected.code
                                                            : null
                                                        );
                                                      }}
                                                    />
                                                  </Td>
                                                  <Td>
                                                    <PartySelectById
                                                      size="xs"
                                                      label=""
                                                      selectedPartyId={
                                                        oracle.party ?? ""
                                                      }
                                                      onChange={(id) => {
                                                        updateOracleField(
                                                          apdIdx,
                                                          stageIdx,
                                                          activityIdx,
                                                          oracleIdx,
                                                          "party",
                                                          id || null
                                                        );
                                                      }}
                                                    />
                                                  </Td>

                                                  <Td>
                                                    <Tooltip label="Delete Oracle">
                                                      <IconButton
                                                        icon={
                                                          <HiOutlineTrash />
                                                        }
                                                        size="xs"
                                                        onClick={() => {
                                                          const updated = [
                                                            ...localProduct.activityPatternDefinitions,
                                                          ];
                                                          updated[
                                                            apdIdx
                                                          ].stages[
                                                            stageIdx
                                                          ].activities[
                                                            activityIdx
                                                          ].oracles.splice(
                                                            oracleIdx,
                                                            1
                                                          );
                                                          handleChange(
                                                            "activityPatternDefinitions",
                                                            updated
                                                          );
                                                        }}
                                                      />
                                                    </Tooltip>
                                                  </Td>
                                                </Tr>
                                              )
                                            )}
                                          </Tbody>
                                        </Table>
                                      </Box>

                                      <HStack justify="flex-end">
                                        <Tooltip label="Delete activity...">
                                          <IconButton
                                            icon={<HiOutlineTrash />}
                                            size="xs"
                                            onClick={() => {
                                              const updated = [
                                                ...localProduct.activityPatternDefinitions,
                                              ];
                                              updated[apdIdx].stages[
                                                stageIdx
                                              ].activities.splice(
                                                activityIdx,
                                                1
                                              );
                                              handleChange(
                                                "activityPatternDefinitions",
                                                updated
                                              );
                                            }}
                                          />
                                        </Tooltip>
                                      </HStack>
                                    </Box>
                                  )
                                )}
                              </TabPanel>
                            ))}
                          </TabPanels>
                        </Tabs>

                        <HStack justify="flex-end">
                          <Tooltip label="Delete activity pattern...">
                            <IconButton
                              icon={<HiOutlineTrash />}
                              size="xs"
                              onClick={() => {
                                const newActivityPatterns = [
                                  ...localProduct.activityPatternDefinitions,
                                ];
                                newActivityPatterns.splice(apdIdx, 1);
                                handleChange(
                                  "activityPatternDefinitions",
                                  newActivityPatterns
                                );
                              }}
                            />
                          </Tooltip>
                        </HStack>
                      </Box>
                    )
                  )}
                </TabPanel>

                <TabPanel>
                  <HStack mb={4}>
                    <Button
                      leftIcon={<HiOutlinePlus />}
                      colorScheme="blue"
                      size="md"
                      onClick={() => {
                        const updated = [...(localProduct.controls || [])];
                        updated.push({
                          id: crypto.randomUUID(),
                          name: "",
                          registerType: "",
                          entryType: "",
                          signal: "",
                        });
                        handleChange("controls", updated);
                      }}
                    >
                      Create
                    </Button>
                  </HStack>

                  {Array.isArray(localProduct.controls) &&
                  localProduct.controls.length > 0 ? (
                    <Box
                      className="ag-theme-quartz"
                      style={{ height: "400px", width: "100%" }}
                    >
                      <AgGridReact
                        rowData={localProduct.controls}
                        columnDefs={controlsColumnDefs}
                        defaultColDef={registerDefaultColDef}
                        rowHeight={50}
                        pagination={true}
                        paginationPageSize={10}
                        animateRows={true}
                        rowSelection="single"
                        getRowId={(params) => params.data.id}
                        onCellValueChanged={(params) => onCellValueChanged(params, "controls")}
                      />
                    </Box>
                  ) : (
                    <Text color="gray.500" fontSize="sm" fontStyle="italic">
                      No controls defined yet.
                    </Text>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Box>
      </Box>

      {/* Create Template Document Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Template Document</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <DocumentDefinitionSelectById
                label="Document Definition"
                selectedDocumentDefinitionId={newTemplateDefinitionId}
                onChange={(id) => setNewTemplateDefinitionId(id)}
                templateType="template"
              />

              <FormControl isRequired>
                <FormLabel>Template Name</FormLabel>
                <Input
                  placeholder="Enter template name..."
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleCreateTemplateDocument();
                    }
                  }}
                />
              </FormControl>

              <Text fontSize="sm" color="gray.600">
                This will create a new template document with the selected document definition and set the template flag to true.
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onClose}
              isDisabled={creating}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateTemplateDocument}
              isLoading={creating}
              loadingText="Creating..."
            >
              Create Template
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProductDefinitionEditor;
